import type { Nullable } from "@core/domain/types/nullable.type";
import type { Undefinable } from "@core/domain/types/undefinable.type";
import { BaseViewModel } from "@iot-platform/core";
import { Pest, PestCodeEnum, Pests } from "@station/domain/models/pest.model";
import type { Station } from "@station/domain/models/station.model";
import { PestGetAllUseCase } from "@station/domain/usecases/pest-get-all.usecase";
import { StationGetByUseCase } from "@station/domain/usecases/station-get-by.usecase";
import { inject, injectable } from "inversify";
import { action, makeObservable, observable, runInAction } from "mobx";

@injectable()
export class PestViewmodel extends BaseViewModel {
    @observable
    pests: Pests = Pests.empty();

    @observable
    initialLoading = false;

    @observable
    station: Nullable<Station> = null;

    @observable
    abioticStress: Undefinable<Readonly<Pest>> = undefined;

    stationId: Nullable<number> = null;

    constructor(
        @inject(PestGetAllUseCase)
        private readonly pestGetAllUseCase: PestGetAllUseCase,
        @inject(StationGetByUseCase)
        private readonly stationGetByUseCase: StationGetByUseCase,
    ) {
        super();
        makeObservable(this);
    }

    @action
    override async didMount(): Promise<void> {
        await this.initViewData();
    }

    private async initViewData(): Promise<void> {
        if (!this.stationId) return;

        runInAction(() => {
            this.initialLoading = true;
        });

        await this.setStation(this.stationId);
        await this.getAllPests();
    }

    setStationId(stationId: number): void {
        this.stationId = stationId;
    }

    @action
    async getAllPests(): Promise<void> {
        if (this.station) {
            const pestsResult = await this.pestGetAllUseCase.execute(
                this.station.id.value,
            );

            runInAction(() => {
                this.pests = pestsResult;
                this.abioticStress = this.pests.agents.find(
                    (pest) => pest.code === PestCodeEnum.ABIOTIC_STRESS,
                );
                this.initialLoading = false;
            });
        }
    }

    @action
    async setStation(stationId: number): Promise<void> {
        const station = await this.stationGetByUseCase.execute(stationId);

        if (!station) {
            throw new Error("Station not found");
        }
        runInAction(() => {
            this.station = station;
        });
    }
}
